/**
 * WhatsApp Live: every customer chat on the central numbers, who has it, and head office's
 * actions (open, take over, reassign, close, reply). Moved out of the WhatsApp page to the
 * Dashboard's Leads tab (Gary, 15 Oct); the parent owns the chats so it can show the counts.
 * Every action goes through the messaging-send edge function.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../../components/Toast';
import Modal from '../../../../components/Modal';
import SearchSelect from '../../../../components/SearchSelect';
import type { LiveChat, Operator } from './liveData';
import { IN_PLAY, chatAction, loadOperators } from './liveData';
import LiveView from './LiveView';
import ChatPanel from './ChatPanel';

/** Chats that need attention now: unassigned, overdue or escalated (Gary, 14 Oct). */
export function needsAttention(chats: LiveChat[]): number {
  return chats.filter((c) => IN_PLAY.includes(c.stage)
    && (c.stage === 'unassigned' || c.escalated || (c.dueMinutes !== null && c.dueMinutes <= 0))).length;
}

interface Props {
  chats: LiveChat[];
  loading: boolean;
  reload: () => Promise<void> | void;
}

export default function LiveDesk({ chats, loading, reload }: Props) {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [openId, setOpenId] = useState<string | null>(null);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [reassigning, setReassigning] = useState<{ chat: LiveChat; userId: string | null } | null>(null);
  const [closing, setClosing] = useState<{ chat: LiveChat; outcome: 'booked' | 'closed'; reason: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const closePanel = useCallback(() => setOpenId(null), []);

  useEffect(() => { void loadOperators().then(setOperators); }, []);

  const open = chats.find((c) => c.id === openId) ?? null;

  async function run(chat: LiveChat, body: Parameters<typeof chatAction>[1], done: string): Promise<boolean> {
    setBusy(true);
    const problem = await chatAction(chat.id, body);
    setBusy(false);
    if (problem) { showToast(problem, 'error'); return false; }
    showToast(done, 'success');
    void reload();
    return true;
  }

  const onTakeOver = (c: LiveChat) => {
    void run(c, { action: 'take_over' }, 'You have this chat now. Reply from the panel.').then((ok) => { if (ok) setOpenId(c.id); });
  };
  const onReassign = (c: LiveChat) => setReassigning({ chat: c, userId: null });
  const onCloseChat = (c: LiveChat) => setClosing({ chat: c, outcome: 'closed', reason: '' });
  const onOpenLead = (c: LiveChat) => { if (c.leadId) navigate(`/leads?id=${c.leadId}`); };
  const onSend = (c: LiveChat, text: string, signWithName: boolean) => run(c, { action: 'send', text, sign_with_name: signWithName }, 'Sent');
  const onDrc = (c: LiveChat, action: 'return_to_ai' | 'ai_off') => {
    void run(c, { action }, action === 'return_to_ai' ? 'Leo has the chat again' : 'Leo is off for this chat');
  };

  // POS chats can go to anyone at head office's choice; the POS team is listed first.
  const operatorOptions = (reassigning?.chat.brand === 'pos'
    ? [...operators].sort((a, b) => Number(b.posStaff) - Number(a.posStaff))
    : operators)
    .filter((o) => o.id !== reassigning?.chat.assignedUserId)
    .map((o) => ({ value: o.id, label: o.posStaff ? `${o.name} (Peace of Sleep)` : o.name }));

  return (
    <>
      <LiveView
        chats={chats}
        loading={loading}
        selectedId={openId}
        onView={(c) => setOpenId(c.id)}
        onReassign={onReassign}
        onTakeOver={onTakeOver}
        onOpenLead={onOpenLead}
      />
      {open && (
        <ChatPanel
          chat={open}
          onClose={closePanel}
          onReassign={onReassign}
          onTakeOver={onTakeOver}
          onCloseChat={onCloseChat}
          onOpenLead={onOpenLead}
          onSend={onSend}
          onDrc={onDrc}
        />
      )}

      {reassigning && (
        <Modal
          title={`Reassign ${reassigning.chat.name}`}
          size="sm"
          onClose={() => setReassigning(null)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setReassigning(null)}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!reassigning.userId || busy}
                onClick={() => {
                  const { chat, userId } = reassigning;
                  if (!userId) return;
                  void run(chat, { action: 'reassign', user_id: userId }, 'Reassigned').then((ok) => { if (ok) setReassigning(null); });
                }}
              >
                Reassign
              </button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">Give this chat to</label>
            <SearchSelect
              options={operatorOptions}
              value={reassigning.userId}
              onChange={(v) => setReassigning({ ...reassigning, userId: v })}
              placeholder="Choose an operator"
            />
            <span className="form-help">They are told on their phone, and the reply timer starts for them.</span>
          </div>
        </Modal>
      )}

      {closing && (
        <Modal
          title={`Close ${closing.chat.name}`}
          size="sm"
          onClose={() => setClosing(null)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setClosing(null)}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={() => {
                  const { chat, outcome, reason } = closing;
                  void run(chat, { action: 'close', outcome, reason: reason.trim() || undefined }, outcome === 'booked' ? 'Marked booked' : 'Chat closed')
                    .then((ok) => { if (ok) { setClosing(null); setOpenId(null); } });
                }}
              >
                {closing.outcome === 'booked' ? 'Mark booked' : 'Close chat'}
              </button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">Outcome</label>
            <SearchSelect
              options={[{ value: 'booked', label: 'Booked' }, { value: 'closed', label: 'Closed, not booked' }]}
              value={closing.outcome}
              onChange={(v) => setClosing({ ...closing, outcome: v === 'booked' ? 'booked' : 'closed' })}
              searchable={false}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="wa-close-reason">Reason (optional)</label>
            <input
              id="wa-close-reason"
              className="form-input"
              value={closing.reason}
              onChange={(e) => setClosing({ ...closing, reason: e.target.value })}
              placeholder="For example: price, booked elsewhere, no reply"
            />
          </div>
        </Modal>
      )}
    </>
  );
}
